/**
 * Custom Cart Drawer
 *
 * Server-rendered section HTML is the source of truth. Internal mutations
 * (qty changes, removals, gift picker adds) POST with `sections: this.sectionId`;
 * the response includes server-rendered HTML which we patch into the live drawer
 * via sub-section swaps (with the meter kept out of the swap so CSS transitions
 * still fire on the fill widths).
 *
 * Threshold-drop gift auto-removal is atomic: predictGiftRemoval inspects the
 * DOM before the mutation to decide whether to use /cart/update.js (which lets
 * us change the line qty AND remove the gift in a single request). No multi-step
 * async chains, no races with subsequent user actions.
 *
 * Only the qty input value updates optimistically. Subtotals, line prices,
 * picker state, progress bar, milestones — all wait for the server response.
 * The 300ms debounce + atomic single-request mutations hide the latency.
 */

class CustomCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.isAddingToCart = false;
    this.sectionId = null;
    this.giftHandles = [];
    this.freeGiftThresholdCents = 0;
    this.giftUnlockMode = 'subtotal';
    this.freeGiftMinQty = 0;
    this.cartLevelDiscountCents = 0;
    this._queuedUpdates = new Map();
    this._debounceTimers = new Map();
    this._selfTriggeredCount = 0;
    this._lastRenderedHTML = null;
    this._checkoutNudged = false;
    this._toastTimer = null;
    this._progressStagger = null;
  }

  connectedCallback() {
    this.sectionId = this.extractSectionId();
    this.backdrop = this.querySelector('[data-ccd-backdrop]');
    this.drawer = this.querySelector('[data-ccd-drawer]');

    this.giftHandles = (this.dataset.giftHandles || '')
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean);
    this.freeGiftThresholdCents = parseInt(this.dataset.freeGiftThresholdCents, 10) || 0;
    // 'quantity' = buy N get 1 free (N = freeGiftMinQty non-gift units), 'subtotal' = dollar threshold
    this.giftUnlockMode = this.dataset.giftUnlockMode === 'quantity' ? 'quantity' : 'subtotal';
    this.freeGiftMinQty = parseInt(this.dataset.freeGiftMinQty, 10) || 0;
    this.cartLevelDiscountCents = parseInt(this.dataset.cartDiscountCents, 10) || 0;

    this.bindEvents();
    this.bindThemeEvents();
    this.interceptCartIconClick();

    this.signupDiscountCode = (this.dataset.signupDiscountCode || '').trim();
    if (this.signupDiscountCode) this.initSignupDiscount();

    // Upsell tracks (re)render via patchUpsells and the async
    // <product-recommendations> fetch — watch for new ones and loop-ify them.
    this._carouselObserver = new MutationObserver(() => this.initCarouselLoops());
    this._carouselObserver.observe(this, { childList: true, subtree: true });
    this.initCarouselLoops();
  }

  /* ====================================================================
     Signup Discount Auto-Apply
     After a Klaviyo popup signup or the footer newsletter signup, the
     configured code is applied to the session server-side: the /discount/
     route sets Shopify's checkout discount cookie (survives page loads,
     applies at checkout natively) and /cart/update.js attaches it to the
     cart so the drawer's footer can show it. localStorage only stores a
     small retry marker in case the apply request fails mid-signup.
     ==================================================================== */

  initSignupDiscount() {
    // Klaviyo popup: public form event fired on successful submit. When a
    // form ID is configured, only that form triggers the apply (other
    // Klaviyo forms — back-in-stock, quizzes — shouldn't hand out the code).
    const klaviyoFormId = (this.dataset.klaviyoFormId || '').trim();
    window.addEventListener('klaviyoForms', (e) => {
      if (!e.detail || e.detail.type !== 'submittedForm') return;
      if (klaviyoFormId && e.detail.formId !== klaviyoFormId) return;
      this.applySignupDiscount();
    });

    // Footer newsletter ({% form 'customer' %}): success reloads with ?customer_posted=true
    if (/[?&]customer_posted=true/.test(window.location.search)) {
      this.applySignupDiscount();
    }

    // Retry: a previous signup's apply didn't complete (e.g. navigation raced it)
    let marker = null;
    try { marker = localStorage.getItem('ccd-signup-discount'); } catch (err) { /* storage blocked */ }
    if (marker === 'pending:' + this.signupDiscountCode) {
      this.applySignupDiscount();
    }
  }

  async applySignupDiscount() {
    const code = this.signupDiscountCode;
    if (!code || this._applyingSignupDiscount) return;
    this._applyingSignupDiscount = true;

    try { localStorage.setItem('ccd-signup-discount', 'pending:' + code); } catch (err) { /* storage blocked */ }

    try {
      // 1. Checkout discount cookie — guarantees the code is applied at checkout
      await fetch(`/discount/${encodeURIComponent(code)}`);

      // 2. Attach to the cart so the drawer footer reflects it immediately
      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ discount: code, sections: this.sectionId }),
        });
        const data = await res.json();
        const sectionHtml = data.sections?.[this.sectionId];
        if (sectionHtml) {
          this.replaceDrawerContent(sectionHtml);
        } else {
          await this.refreshSection();
        }
      } catch (err) {
        // Cart-level attach unsupported/failed — cookie still applies at checkout
        await this.refreshSection();
      }

      try { localStorage.setItem('ccd-signup-discount', 'applied:' + code); } catch (err) { /* storage blocked */ }
    } catch (err) {
      console.error('Signup discount apply error:', err);
    } finally {
      this._applyingSignupDiscount = false;
    }
  }

  extractSectionId() {
    const wrapper = this.closest('[id^="shopify-section-"]');
    if (wrapper) return wrapper.id.replace('shopify-section-', '');
    return this.dataset.sectionId;
  }

  /* ====================================================================
     Event Binding
     ==================================================================== */

  bindEvents() {
    this.backdrop?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    this.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('[data-ccd-close]');
      if (closeBtn) { e.preventDefault(); this.close(); return; }

      const minusBtn = e.target.closest('[data-ccd-qty-minus]');
      if (minusBtn) { this.handleQuantityChange(minusBtn.dataset.key, -1); return; }

      const plusBtn = e.target.closest('[data-ccd-qty-plus]');
      if (plusBtn) { this.handleQuantityChange(plusBtn.dataset.key, 1); return; }

      const removeBtn = e.target.closest('[data-ccd-remove]');
      if (removeBtn) { this.handleRemoveItem(removeBtn.dataset.key); return; }

      // Whole gift card is clickable — not just the Add button. We use the
      // inner Add button (if present) for the "Adding..." loading state so we
      // don't overwrite the card's markup.
      const giftCard = e.target.closest('[data-ccd-gift-card]');
      if (giftCard) {
        const giftBtn = giftCard.querySelector('[data-ccd-gift-add-btn]');
        this.handleAddToCart(giftCard.dataset.variantId, true, giftBtn);
        return;
      }

      // Upsell carousel qty stepper (local only — nothing hits the cart until Add)
      const upsellMinus = e.target.closest('[data-ccd-upsell-minus]');
      if (upsellMinus) { this.stepUpsellQty(upsellMinus, -1); return; }

      const upsellPlus = e.target.closest('[data-ccd-upsell-plus]');
      if (upsellPlus) { this.stepUpsellQty(upsellPlus, 1); return; }

      const upsellBtn = e.target.closest('[data-ccd-add-upsell]');
      if (upsellBtn) {
        const slide = upsellBtn.closest('[data-ccd-slide]');
        const qtyInput = slide?.querySelector('[data-ccd-upsell-qty]');
        const qty = Math.max(1, parseInt(qtyInput?.value, 10) || 1);
        this.handleAddToCart(upsellBtn.dataset.variantId, false, upsellBtn, qty);
        return;
      }

      const emptyAddBtn = e.target.closest('[data-ccd-add-empty]');
      if (emptyAddBtn) { this.handleAddToCart(emptyAddBtn.dataset.variantId, false, emptyAddBtn); return; }

      // Gift carousel arrows (4+ gifts)
      const giftArrow = e.target.closest('[data-ccd-gift-prev], [data-ccd-gift-next]');
      if (giftArrow) { this.moveGiftCarousel(giftArrow.hasAttribute('data-ccd-gift-next') ? 1 : -1); return; }

      // Carousel arrows / dots
      const prevBtn = e.target.closest('[data-ccd-car-prev]');
      if (prevBtn) { this.moveCarousel(prevBtn, -1); return; }

      const nextBtn = e.target.closest('[data-ccd-car-next]');
      if (nextBtn) { this.moveCarousel(nextBtn, 1); return; }

      const dotBtn = e.target.closest('[data-ccd-car-dot]');
      if (dotBtn) { this.jumpCarousel(dotBtn); return; }

      // Checkout nudge: first attempt while gift-eligible without a gift shows
      // the toast instead (customers have missed that the free item must be
      // added). A second attempt goes straight through.
      const checkoutBtn = e.target.closest('[data-ccd-checkout]');
      if (checkoutBtn) {
        if (
          this.hasAttribute('data-gift-eligible') &&
          !this.hasAttribute('data-has-gift') &&
          !this._checkoutNudged
        ) {
          e.preventDefault();
          this._checkoutNudged = true;
          this.showGiftToast();
        }
        return;
      }

      const bundleToggle = e.target.closest('[data-ccd-bundle-toggle]');
      if (bundleToggle) { this.toggleBundle(bundleToggle); return; }
    });

    this.addEventListener('change', (e) => {
      const subToggle = e.target.closest('[data-ccd-sub-toggle]');
      if (subToggle) { this.handleSubscriptionToggle(subToggle); return; }

      const planRadio = e.target.closest('[data-ccd-plan]');
      if (planRadio) { this.changeSellingPlan(planRadio.dataset.key, planRadio.value, planRadio.dataset.planPrice); return; }

      const qtyInput = e.target.closest('[data-ccd-qty-input]');
      if (qtyInput) {
        const qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
        qtyInput.value = qty;
        this.setItemLoading(qtyInput.dataset.key, true);
        this.debouncedUpdateItem(qtyInput.dataset.key, qty);
        return;
      }

      const upsellQty = e.target.closest('[data-ccd-upsell-qty]');
      if (upsellQty) {
        upsellQty.value = Math.max(1, parseInt(upsellQty.value, 10) || 1);
      }
    });

    // Keep the carousel dots in sync while the customer swipes. Scroll events
    // don't bubble, so listen in the capture phase at the host level.
    this.addEventListener('scroll', (e) => {
      if (!(e.target instanceof Element)) return;
      if (e.target.hasAttribute('data-ccd-car-track')) this.syncCarouselDots(e.target);
      else if (e.target.hasAttribute('data-ccd-gift-track')) this.syncGiftCarousel();
    }, true);
  }

  /* ====================================================================
     Theme Event Integration
     ==================================================================== */

  bindThemeEvents() {
    document.addEventListener('cart:prepare-bundled-sections', (e) => {
      if (this.sectionId) e.detail.sections.add(this.sectionId);
    });

    document.documentElement.addEventListener('cart:change', (e) => {
      const detail = e.detail;
      if (!detail) return;

      if (this._selfTriggeredCount > 0) {
        this._selfTriggeredCount--;
        return;
      }

      // External mutation (e.g. product-page add): apply server-rendered HTML
      const sectionHtml = detail.cart?.sections?.[this.sectionId];
      if (sectionHtml) {
        this.replaceDrawerContent(sectionHtml);
      } else {
        this.refreshSection();
      }

      if (detail.baseEvent === 'variant:add' && !this.isOpen) {
        requestAnimationFrame(() => this.open());
      }
    });

    document.documentElement.addEventListener('cart:refresh', () => {
      this.refreshSection();
    });

    window.addEventListener('pageshow', (e) => {
      if (e.persisted) this.refreshSection();
    });
  }

  /* ====================================================================
     Cart Icon Click Interception
     ==================================================================== */

  interceptCartIconClick() {
    document.addEventListener('click', (e) => {
      const cartLink = e.target.closest('a[href="/cart"], a[href$="/cart"]');
      if (!cartLink) return;
      if (window.location.pathname === '/cart') return;

      const isHeaderLink = cartLink.closest('.header, header, [class*="header"]');
      if (isHeaderLink) {
        e.preventDefault();
        e.stopPropagation();
        this.open();
      }
    }, true);
  }

  /* ====================================================================
     Open / Close
     ==================================================================== */

  open() {
    this.isOpen = true;
    this._checkoutNudged = false;  // each drawer visit nudges at most once
    this.backdrop?.classList.add('is-visible');
    this.drawer?.classList.add('is-open');
    document.body.classList.add('ccd-open');

    // Looped tracks initialized while the drawer had no layout (zero width)
    // couldn't take their starting offset — normalize onto the first real slide.
    this.querySelectorAll('[data-ccd-loop]').forEach((track) => {
      if (track.clientWidth && track.scrollLeft < track.clientWidth / 2) {
        track.scrollLeft = track.clientWidth;
      }
    });

    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
  }

  close() {
    this.isOpen = false;
    this.backdrop?.classList.remove('is-visible');
    this.drawer?.classList.remove('is-open');
    document.body.classList.remove('ccd-open');
    document.dispatchEvent(new CustomEvent('cart-drawer:close'));
  }

  /* ====================================================================
     Checkout Nudge Toast
     ==================================================================== */

  showGiftToast() {
    const toast = this.querySelector('[data-ccd-toast]');
    if (!toast) return;
    toast.classList.add('is-visible');
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 4000);

    // Bring the picker into view so the free-item choice is obvious
    this.querySelector('[data-ccd-gifts]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /* ====================================================================
     Upsell Carousel — infinite loop
     A clone of the last real slide is prepended and a clone of the first
     appended. The track starts on the first real slide; when a scroll
     settles on a clone we jump (instantly, no animation) to its real
     counterpart, so arrows AND swipes wrap seamlessly in both directions.
     Dots are rendered for real slides only and map through the offset.
     ==================================================================== */

  initCarouselLoops() {
    this.querySelectorAll('[data-ccd-car-track]:not([data-ccd-loop])').forEach((track) => {
      const slides = track.querySelectorAll('[data-ccd-slide]');
      if (slides.length < 2) return;
      track.setAttribute('data-ccd-loop', slides.length);

      const firstClone = slides[0].cloneNode(true);
      const lastClone = slides[slides.length - 1].cloneNode(true);
      firstClone.setAttribute('data-ccd-clone', '');
      lastClone.setAttribute('data-ccd-clone', '');
      track.appendChild(firstClone);
      track.insertBefore(lastClone, track.firstChild);

      // Start on the first real slide (offset past the prepended clone)
      requestAnimationFrame(() => {
        if (track.clientWidth) track.scrollLeft = track.clientWidth;
      });

      const settle = () => this.wrapCarousel(track);
      if ('onscrollend' in track) {
        track.addEventListener('scrollend', settle);
      } else {
        let timer;
        track.addEventListener('scroll', () => {
          clearTimeout(timer);
          timer = setTimeout(settle, 140);
        });
      }
    });
  }

  wrapCarousel(track) {
    const w = Math.max(1, track.clientWidth);
    const realCount = parseInt(track.getAttribute('data-ccd-loop'), 10) || 0;
    if (!realCount) return;
    const raw = Math.round(track.scrollLeft / w);
    // Direct scrollLeft assignment is instant — no smooth animation, so the
    // clone→real swap is invisible.
    if (raw === 0) track.scrollLeft = realCount * w;
    else if (raw === realCount + 1) track.scrollLeft = w;
  }

  stepUpsellQty(button, delta) {
    const slide = button.closest('[data-ccd-slide]');
    const input = slide?.querySelector('[data-ccd-upsell-qty]');
    if (!input) return;
    input.value = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
  }

  moveCarousel(button, direction) {
    const track = button.closest('[data-ccd-carousel]')?.querySelector('[data-ccd-car-track]');
    if (!track) return;
    track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
  }

  jumpCarousel(dot) {
    const upsells = dot.closest('[data-ccd-upsells]');
    const track = upsells?.querySelector('[data-ccd-car-track]');
    if (!track) return;
    const index = parseInt(dot.dataset.ccdDotIndex, 10) || 0;
    const offset = track.hasAttribute('data-ccd-loop') ? 1 : 0;
    track.scrollTo({ left: (index + offset) * track.clientWidth, behavior: 'smooth' });
  }

  syncCarouselDots(track) {
    const upsells = track.closest('[data-ccd-upsells]');
    const dots = upsells?.querySelectorAll('[data-ccd-car-dot]');
    if (!dots || !dots.length) return;
    let index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    const realCount = parseInt(track.getAttribute('data-ccd-loop'), 10) || 0;
    if (realCount) index = (((index - 1) % realCount) + realCount) % realCount;
    dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
  }

  moveGiftCarousel(direction) {
    const track = this.querySelector('[data-ccd-gift-track]');
    const card = track?.querySelector('[data-ccd-gift-card]');
    if (!card) return;
    // One card per press; the browser clamps the last step to the track end.
    track.scrollBy({ left: direction * card.offsetWidth, behavior: 'smooth' });
  }

  /**
   * Mirror the gift track's scroll position onto the host as data-gift-car
   * (absent = start, "mid", "end"). CSS uses it to fade the peeking edge(s)
   * and hide the arrow that has nowhere to go.
   */
  syncGiftCarousel() {
    const track = this.querySelector('[data-ccd-gift-track]');
    const max = track ? track.scrollWidth - track.clientWidth : 0;
    if (!track || max <= 0 || track.scrollLeft <= 2) this.removeAttribute('data-gift-car');
    else this.setAttribute('data-gift-car', track.scrollLeft >= max - 2 ? 'end' : 'mid');
  }

  /* ====================================================================
     Bundle Toggle
     ==================================================================== */

  toggleBundle(button) {
    const bundleItems = button.parentElement?.querySelector('[data-ccd-bundle-items]');
    if (!bundleItems) return;
    const isExpanded = button.classList.toggle('is-expanded');
    bundleItems.style.display = isExpanded ? 'block' : 'none';
    const itemCount = bundleItems.querySelectorAll('.ccd-bundle__component').length;
    const svg = button.querySelector('svg').outerHTML;
    button.innerHTML = (isExpanded ? `Hide ${itemCount} items` : `Show ${itemCount} items`) + svg;
  }

  /* ====================================================================
     Loading Indicators
     ==================================================================== */

  setItemLoading(key, loading) {
    const item = this.querySelector(`[data-line-key="${key}"]`);
    if (!item) return;
    item.classList.toggle('ccd-item--loading', loading);
  }

  setDrawerLoading(loading) {
    const body = this.querySelector('[data-ccd-body]');
    if (!body) return;
    body.classList.toggle('ccd-body--loading', loading);
  }

  /* ====================================================================
     Gift detection + threshold-drop prediction
     ==================================================================== */

  isGiftHandle(handle) {
    return !!handle && this.giftHandles.includes(handle);
  }

  /**
   * From current DOM, predict whether changing line `key` to `newQty` will drop
   * the cart below the gift requirement while a gift line is in the cart.
   * Returns the gift line key to remove, or null.
   *
   * Subtotal mode: totals are rebuilt from each row's data-original-unit-price
   * × qty minus the last known cart-level discount — mirroring the Liquid
   * eligibility math. Quantity mode (buy N get 1 free): non-gift units are
   * summed and compared with freeGiftMinQty.
   *
   * Used to decide between /cart/change.js (normal qty change) and
   * /cart/update.js (atomic qty change + gift removal).
   */
  predictGiftRemoval(key, newQty, unitPriceOverride) {
    const byQuantity = this.giftUnlockMode === 'quantity';
    if (byQuantity ? this.freeGiftMinQty <= 0 : this.freeGiftThresholdCents <= 0) return null;
    if (!this.giftHandles.length) return null;

    const targetLine = this.querySelector(`[data-line-key="${key}"]`);
    if (!targetLine) return null;
    // Changing the gift line itself doesn't affect the non-gift total
    if (this.isGiftHandle(targetLine.dataset.productHandle)) return null;

    // A selling-plan swap changes the line's unit price (subscription discount
    // on, or back to one-time) — the caller passes the price the line will have.
    const targetUnit = Number.isFinite(unitPriceOverride)
      ? unitPriceOverride
      : parseInt(targetLine.dataset.originalUnitPrice, 10) || 0;
    let newTotalCents = newQty * targetUnit;
    let newItemCount = newQty;
    let giftLineKey = null;

    this.querySelectorAll('[data-line-key]').forEach((el) => {
      if (el.dataset.lineKey === key) return;          // already counted as newQty
      if (el.dataset.removing === 'true') return;       // mid-animation, treat as gone

      const handle = el.dataset.productHandle;
      if (this.isGiftHandle(handle)) {
        if (!giftLineKey) giftLineKey = el.dataset.lineKey;
      } else {
        const input = el.querySelector('[data-ccd-qty-input]');
        const qty = input ? parseInt(input.value, 10) || 0 : 1;
        const unit = parseInt(el.dataset.originalUnitPrice, 10) || 0;
        newTotalCents += qty * unit;
        newItemCount += qty;
      }
    });

    newTotalCents -= this.cartLevelDiscountCents;

    if (!giftLineKey) return null;
    if (byQuantity) return newItemCount < this.freeGiftMinQty ? giftLineKey : null;
    if (newTotalCents < this.freeGiftThresholdCents) return giftLineKey;
    return null;
  }

  /* ====================================================================
     Cart mutations
     ==================================================================== */

  handleQuantityChange(key, delta) {
    const input = this.querySelector(`[data-ccd-qty-input][data-key="${key}"]`);
    if (!input) return;

    const currentQty = parseInt(input.value, 10) || 1;
    const newQty = Math.max(0, currentQty + delta);

    if (newQty === 0) {
      this.handleRemoveItem(key);
      return;
    }

    // Only optimistic update: the qty input value. Everything else waits for
    // server-rendered HTML so the BXGY-aware pricing stays authoritative.
    input.value = newQty;
    this.setItemLoading(key, true);
    this.debouncedUpdateItem(key, newQty);
  }

  debouncedUpdateItem(key, quantity) {
    this._queuedUpdates.set(key, quantity);
    const existing = this._debounceTimers.get(key);

    // First click fires immediately — no delay for single taps
    if (!existing) {
      this._debounceTimers.set(key, setTimeout(() => {
        this._debounceTimers.delete(key);
      }, 300));
      this._queuedUpdates.delete(key);
      this.updateCartItem(key, quantity);
      return;
    }

    // Rapid follow-up clicks: reset timer, batch into one request
    clearTimeout(existing);
    this._debounceTimers.set(key, setTimeout(() => {
      this._debounceTimers.delete(key);
      const qty = this._queuedUpdates.get(key);
      this._queuedUpdates.delete(key);
      if (qty !== undefined) this.updateCartItem(key, qty);
    }, 300));
  }

  async handleRemoveItem(key) {
    const timer = this._debounceTimers.get(key);
    if (timer) {
      clearTimeout(timer);
      this._debounceTimers.delete(key);
      this._queuedUpdates.delete(key);
    }

    const item = this.querySelector(`[data-line-key="${key}"]`);
    if (item) {
      item.dataset.removing = 'true';
      item.style.transition = 'opacity 0.15s ease, max-height 0.2s ease';
      item.style.opacity = '0';
      item.style.maxHeight = item.offsetHeight + 'px';
      requestAnimationFrame(() => {
        item.style.maxHeight = '0';
        item.style.overflow = 'hidden';
      });
    }

    this.setDrawerLoading(true);
    this.updateCartItem(key, 0);
  }

  /**
   * Resolve the underlying cart line keys backing a (possibly merged) drawer row.
   * The BXGY discount can split one logical line into two cart lines; the section
   * renders them as a single row whose data-line-keys lists every member key, with
   * data-line-key (and the button data-key) set to the primary (first) member.
   * Returns [key] when the row isn't merged or can't be found.
   */
  getLineKeys(key) {
    const row = this.querySelector(`[data-line-key="${key}"]`);
    const raw = row?.dataset.lineKeys;
    if (!raw) return [key];
    const keys = raw.split(',').map((k) => k.trim()).filter(Boolean);
    return keys.length ? keys : [key];
  }

  /**
   * Submit a line item quantity change. If the change would drop the cart below
   * the gift threshold while a gift is in the cart, both changes are batched
   * into a single atomic /cart/update.js request so the server sees the final
   * state in one shot — no chained async, no race with subsequent user actions.
   *
   * Merged rows (one variant split across multiple cart lines by the BXGY
   * discount) distribute the new quantity onto the primary line and zero the
   * rest, collapsing the split back into a single line.
   */
  async updateCartItem(key, quantity) {
    const giftKeyToRemove = this.predictGiftRemoval(key, quantity);
    const lineKeys = this.getLineKeys(key);
    const isMerged = lineKeys.length > 1;

    let url;
    let body;
    if (giftKeyToRemove || isMerged) {
      const updates = {};
      // Put the full quantity on the primary line, zero any extra split lines.
      lineKeys.forEach((k, i) => { updates[k] = i === 0 ? quantity : 0; });
      if (giftKeyToRemove) updates[giftKeyToRemove] = 0;
      url = '/cart/update.js';
      body = JSON.stringify({
        updates,
        sections: this.sectionId,
      });
    } else {
      url = '/cart/change.js';
      body = JSON.stringify({
        id: key,
        quantity,
        sections: this.sectionId,
      });
    }

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body,
      });
      const data = await res.json();

      // Empty cart transition needs the empty-state markup
      if (data.item_count === 0) {
        await this.refreshSection();
        this.dispatchCartChange('line-item:change', data);
        return;
      }

      const sectionHtml = data.sections?.[this.sectionId];
      if (sectionHtml) {
        this.replaceDrawerContent(sectionHtml);
      } else {
        await this.refreshSection();
      }

      this.dispatchCartChange('line-item:change', data);
    } catch (err) {
      console.error('Cart update error:', err);
      await this.refreshSection();
    } finally {
      this.setItemLoading(key, false);
      this.setDrawerLoading(false);
    }
  }

  /* ====================================================================
     Subscribe & save (in-cart selling plan swap)
     The checkbox applies the line's default plan (data-default-plan, the
     section's configured frequency); the pills switch between plans; unchecking
     drops the plan. All go through /cart/change.js with `selling_plan`, which
     re-keys the line — patchItems pairs the old and new rows by variant so the
     row updates in place instead of collapsing and re-entering.
     ==================================================================== */

  handleSubscriptionToggle(checkbox) {
    const key = checkbox.dataset.key;
    const wrap = checkbox.closest('[data-ccd-sub]');
    const plans = wrap?.querySelector('[data-ccd-sub-plans]');

    if (checkbox.checked) {
      // Optimistic reveal with the default plan selected — the server-rendered
      // row replaces this as soon as the swap lands.
      const defaultId = wrap?.dataset.defaultPlan;
      const radio = (defaultId && wrap.querySelector(`[data-ccd-plan][value="${defaultId}"]`))
        || wrap?.querySelector('[data-ccd-plan]');
      if (!radio) { checkbox.checked = false; return; }
      radio.checked = true;
      wrap.classList.add('is-subscribed');
      if (plans) plans.hidden = false;
      this.changeSellingPlan(key, radio.value, radio.dataset.planPrice);
    } else {
      wrap?.classList.remove('is-subscribed');
      if (plans) plans.hidden = true;
      this.changeSellingPlan(key, null, null);
    }
  }

  async changeSellingPlan(key, planId, planUnitPrice) {
    // A pending typed-qty debounce for this line is folded into this request.
    const timer = this._debounceTimers.get(key);
    if (timer) {
      clearTimeout(timer);
      this._debounceTimers.delete(key);
    }
    const queuedQty = this._queuedUpdates.get(key);
    this._queuedUpdates.delete(key);

    const row = this.querySelector(`[data-line-key="${key}"]`);
    if (!row) return;
    const input = row.querySelector('[data-ccd-qty-input]');
    const quantity = Math.max(1, queuedQty ?? (parseInt(input?.value, 10) || 1));
    const variantId = row.dataset.variantId;
    const currentPlanId = parseInt(row.dataset.sellingPlanId, 10) || null;
    const targetPlanId = planId ? parseInt(planId, 10) : null;
    const isMerged = this.getLineKeys(key).length > 1;

    const newUnitPrice = targetPlanId
      ? parseInt(planUnitPrice, 10)
      : parseInt(row.dataset.onetimeUnitPrice, 10);

    row.dataset.planSwap = 'true';
    this.setItemLoading(key, true);

    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    const post = async (url, body) => {
      const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || `${url} failed (${res.status})`);
      }
      return data;
    };

    try {
      let cart = null;

      // Subscription pricing can drop the cart below the free-gift threshold.
      // Remove the gift first so the plan-swap response is the final state.
      const giftKeyToRemove = this.predictGiftRemoval(key, quantity, newUnitPrice);
      if (giftKeyToRemove) {
        cart = await post('/cart/change.js', { id: giftKeyToRemove, quantity: 0 });
      }

      // Every cart mutation can re-key lines (the BXGY discount allocation is
      // part of the key), and one drawer row can be backed by several split
      // lines. So rather than trusting keys, locate the row's lines in the
      // freshest cart state by variant + current plan and convert each one.
      // Shopify merges converted lines that end up identical.
      if (isMerged && !cart) {
        cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      }

      let data = null;
      for (let guard = 0; guard < 8; guard++) {
        let ref;
        if (!cart) {
          // Common case: single line, nothing has moved — use the row's key and
          // honour a quantity the customer may have just typed.
          ref = { id: key, quantity };
        } else {
          const idx = cart.items.findIndex((item) =>
            String(item.variant_id) === variantId &&
            (item.selling_plan_allocation?.selling_plan?.id ?? null) === currentPlanId
          );
          if (idx === -1) break;
          ref = { line: idx + 1, quantity: cart.items[idx].quantity };
        }

        data = await post('/cart/change.js', {
          ...ref,
          selling_plan: targetPlanId,
          sections: this.sectionId,
        });
        cart = data;

        // Nothing changed on this pass (e.g. the plan was already applied) —
        // stop rather than loop on the same line.
        if (currentPlanId === targetPlanId) break;
      }

      if (!data) {
        await this.refreshSection();
        return;
      }

      const sectionHtml = data.sections?.[this.sectionId];
      if (sectionHtml) {
        this.replaceDrawerContent(sectionHtml);
      } else {
        await this.refreshSection();
      }

      this.dispatchCartChange('line-item:change', data);
    } catch (err) {
      console.error('Selling plan change error:', err);
      await this.refreshSection();
    } finally {
      // The row has usually been re-keyed and replaced by now; clear the old
      // one in case the swap failed and it is still mounted.
      this.setItemLoading(key, false);
      if (row.isConnected) delete row.dataset.planSwap;
    }
  }

  async handleAddToCart(variantId, isGift, sourceBtn, quantity = 1) {
    if (this.isAddingToCart) return;
    this.isAddingToCart = true;

    if (sourceBtn) {
      sourceBtn.disabled = true;
      sourceBtn.dataset.originalText = sourceBtn.textContent;
      sourceBtn.textContent = 'Adding...';
    }

    // Safety timeout: reset button + refresh if the operation hangs
    const safetyTimer = setTimeout(() => {
      if (this.isAddingToCart) {
        this.isAddingToCart = false;
        if (sourceBtn) {
          sourceBtn.textContent = sourceBtn.dataset.originalText || 'Add';
          sourceBtn.disabled = false;
        }
        this.refreshSection();
      }
    }, 10000);

    try {
      // Gift picker swap: remove any existing gift first so the customer ends
      // up with exactly one gift line. We don't apply this intermediate cart
      // state — the /cart/add response below has the authoritative HTML.
      if (isGift) {
        await this.removeExistingGift();
      }

      const body = {
        items: [{ id: parseInt(variantId, 10), quantity }],
        sections: this.sectionId,
      };

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body),
      });
      const addData = await addRes.json();

      const sectionHtml = addData.sections?.[this.sectionId];
      if (sectionHtml) {
        this.replaceDrawerContent(sectionHtml);
      } else {
        await this.refreshSection();
      }

      this.dispatchCartChange('variant:add', addData);
    } catch (err) {
      console.error('Add to cart error:', err);
      await this.refreshSection();
    } finally {
      clearTimeout(safetyTimer);
      if (sourceBtn) {
        sourceBtn.textContent = sourceBtn.dataset.originalText || 'Add';
        sourceBtn.disabled = false;
      }
      this.isAddingToCart = false;
    }
  }

  /**
   * Picker-swap helper: find any existing gift line in the DOM and remove it
   * from the cart. Only called from handleAddToCart(isGift=true) so the
   * subsequent /cart/add response is what we apply — no intermediate state.
   */
  async removeExistingGift() {
    const giftEl = Array.from(this.querySelectorAll('[data-line-key]'))
      .find((el) => this.isGiftHandle(el.dataset.productHandle));
    if (!giftEl) return;
    const giftKey = giftEl.dataset.lineKey;
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: giftKey, quantity: 0 }),
    });
  }

  dispatchCartChange(baseEvent, cart) {
    this._selfTriggeredCount++;
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent, cart },
    }));
  }

  /* ====================================================================
     Section rendering — apply server HTML
     ==================================================================== */

  replaceDrawerContent(html) {
    if (html === this._lastRenderedHTML) return;
    this._lastRenderedHTML = html;

    const temp = document.createElement('div');
    temp.innerHTML = html;

    const newComponent = temp.querySelector('custom-cart-drawer');
    if (!newComponent) return;

    // Sync host attributes — CSS reads these for picker visibility/locked state,
    // and predictGiftRemoval reads the refreshed discount total.
    const hadGift = this.hasAttribute('data-has-gift');
    this.toggleAttribute('data-gift-eligible', newComponent.hasAttribute('data-gift-eligible'));
    this.toggleAttribute('data-has-gift', newComponent.hasAttribute('data-has-gift'));
    this.cartLevelDiscountCents = parseInt(newComponent.dataset.cartDiscountCents, 10) || 0;

    // Once a gift lands in the cart, re-arm the checkout nudge for the case
    // where the gift is later removed while the drawer stays open.
    if (!hadGift && this.hasAttribute('data-has-gift')) this._checkoutNudged = false;

    const newDrawer = newComponent.querySelector('.ccd-drawer');
    if (!newDrawer) return;

    // Empty <-> non-empty transition needs a full structural swap
    const currentHasItems = !!this.querySelector('[data-ccd-body]');
    const newHasItems = !!newDrawer.querySelector('[data-ccd-body]');
    if (currentHasItems !== newHasItems) {
      if (this.isOpen) newDrawer.classList.add('is-open');
      const currentDrawer = this.querySelector('.ccd-drawer');
      if (currentDrawer) currentDrawer.replaceWith(newDrawer);
      this.drawer = newDrawer;
      this.syncGiftCarousel();
      return;
    }

    const body = this.querySelector('[data-ccd-body]');
    const scrollTop = body ? body.scrollTop : 0;

    // Meter: stays mounted, only widths/classes/message swap so CSS transitions fire
    this.transitionProgressBar(newDrawer);

    // Line items: surgical diff with enter/exit animations
    this.patchItems(newDrawer);

    // Footer: swap content when it changed
    this.patchFooter(newDrawer);

    // Light sub-sections: full innerHTML swap (only if content differs)
    const lightSections = ['[data-ccd-empty]', '[data-ccd-gifts]'];
    for (const sel of lightSections) {
      const current = this.querySelector(sel);
      const incoming = newDrawer.querySelector(sel);
      if (current && incoming) {
        if (current.outerHTML !== incoming.outerHTML) {
          // Keep the gift carousel where the customer left it across the swap
          const giftScroll = current.querySelector('[data-ccd-gift-track]')?.scrollLeft || 0;
          current.replaceWith(incoming);
          const giftTrack = incoming.querySelector('[data-ccd-gift-track]');
          if (giftTrack && giftScroll) giftTrack.scrollLeft = giftScroll;
        }
      } else if (!current && incoming) {
        const bodyEl = this.querySelector('[data-ccd-body]');
        if (bodyEl) {
          const upsells = bodyEl.querySelector('[data-ccd-upsells]');
          if (upsells) bodyEl.insertBefore(incoming, upsells);
          else bodyEl.appendChild(incoming);
        }
      } else if (current && !incoming) {
        current.remove();
      }
    }

    // Header count
    const newCount = newDrawer.querySelector('[data-ccd-count]');
    const currentCount = this.querySelector('[data-ccd-count]');
    if (newCount && currentCount) currentCount.textContent = newCount.textContent;

    // Upsells (only re-render when the cart's product mix changed)
    this.patchUpsells(newDrawer);

    if (body) body.scrollTop = scrollTop;
    this.syncGiftCarousel();
  }

  patchItems(newDrawer) {
    const currentList = this.querySelector('[data-ccd-items]');
    const incomingList = newDrawer.querySelector('[data-ccd-items]');

    if (!currentList && incomingList) {
      const parent = this.drawer.querySelector('.ccd-body') || this.drawer;
      parent.appendChild(incomingList);
      return;
    }
    if (currentList && !incomingList) { currentList.remove(); return; }
    if (!currentList || !incomingList) return;

    const currentMap = new Map();
    currentList.querySelectorAll('[data-line-key]').forEach((el) => {
      currentMap.set(el.dataset.lineKey, el);
    });

    const incomingMap = new Map();
    incomingList.querySelectorAll('[data-line-key]').forEach((el) => {
      incomingMap.set(el.dataset.lineKey, el);
    });

    // Plan swaps: a selling-plan change re-keys the line. Pair the outgoing
    // row with the incoming row for the same variant and replace it in place
    // so the row doesn't collapse and re-enter.
    const swappedIn = new Set();
    currentMap.forEach((item, key) => {
      if (incomingMap.has(key) || item.dataset.planSwap !== 'true') return;
      const match = Array.from(incomingMap.entries()).find(([inKey, el]) =>
        !currentMap.has(inKey) && !swappedIn.has(inKey) && el.dataset.variantId === item.dataset.variantId
      );
      if (!match) return;
      item.replaceWith(match[1]);
      swappedIn.add(match[0]);
      currentMap.delete(key);
    });

    // Removed items: animate out
    currentMap.forEach((item, key) => {
      if (incomingMap.has(key)) return;
      item.dataset.removing = 'true';
      item.style.transition = 'opacity 0.15s ease, max-height 0.2s ease';
      item.style.opacity = '0';
      item.style.maxHeight = item.offsetHeight + 'px';
      requestAnimationFrame(() => {
        item.style.maxHeight = '0';
        item.style.overflow = 'hidden';
      });
      setTimeout(() => item.remove(), 200);
    });

    // Existing items: replace with server version (preserve bundle toggle state).
    // Skip lines with a pending debounce — the user's typed qty is more recent.
    incomingMap.forEach((incoming, key) => {
      const current = currentMap.get(key);
      if (!current) return;
      if (this._debounceTimers.has(key)) return;

      const bundleExpanded = current.querySelector('[data-ccd-bundle-toggle]')?.getAttribute('aria-expanded') === 'true';
      current.replaceWith(incoming);
      if (bundleExpanded) {
        const newToggle = incoming.querySelector('[data-ccd-bundle-toggle]');
        if (newToggle) {
          newToggle.setAttribute('aria-expanded', 'true');
          const bundleContent = incoming.querySelector('[data-ccd-bundle-content]');
          if (bundleContent) bundleContent.style.display = '';
        }
      }
    });

    // New items: enter animation. Gift rows prepend — the drawer always lists
    // the free gift first (matches the server's two-pass render order).
    incomingMap.forEach((incoming, key) => {
      if (currentMap.has(key) || swappedIn.has(key)) return;
      incoming.style.opacity = '0';
      incoming.style.maxHeight = '0';
      incoming.style.overflow = 'hidden';
      incoming.style.transition = 'opacity 0.2s ease 0.05s, max-height 0.25s ease';
      if (this.isGiftHandle(incoming.dataset.productHandle)) {
        currentList.prepend(incoming);
      } else {
        currentList.appendChild(incoming);
      }
      requestAnimationFrame(() => {
        incoming.style.maxHeight = '200px';
        incoming.style.opacity = '1';
        setTimeout(() => {
          incoming.style.removeProperty('max-height');
          incoming.style.removeProperty('overflow');
          incoming.style.removeProperty('transition');
          incoming.style.removeProperty('opacity');
        }, 300);
      });
    });
  }

  patchFooter(newDrawer) {
    const currentFooter = this.querySelector('[data-ccd-footer]');
    const incomingFooter = newDrawer.querySelector('[data-ccd-footer]');

    if (!currentFooter && incomingFooter) { this.drawer.appendChild(incomingFooter); return; }
    if (currentFooter && !incomingFooter) { currentFooter.remove(); return; }
    if (!currentFooter || !incomingFooter) return;

    // Row structure varies with state (discount row, shipping row, code note),
    // so swap the whole footer content whenever anything differs.
    if (currentFooter.innerHTML !== incomingFooter.innerHTML) {
      currentFooter.innerHTML = incomingFooter.innerHTML;
    }
  }

  patchUpsells(newDrawer) {
    const current = this.querySelector('[data-ccd-upsells]');
    const incoming = newDrawer.querySelector('[data-ccd-upsells]');

    if (current && incoming) {
      // Re-render only when the cart's product mix changed (data-ccd-sig).
      // Swapping in `incoming` remounts <product-recommendations>, which
      // re-fetches complementary recs — genuine recs still win, with the
      // configured fallbacks filling remaining slides.
      const currentSig = current.getAttribute('data-ccd-sig');
      const incomingSig = incoming.getAttribute('data-ccd-sig');
      if (currentSig !== incomingSig) {
        current.replaceWith(incoming);
      }
    } else if (!current && incoming) {
      const body = this.querySelector('[data-ccd-body]');
      if (body) body.appendChild(incoming);
    } else if (current && !incoming) {
      current.remove();
    }
  }

  transitionProgressBar(newDrawer) {
    // A staged sequence from a previous update may still be pending — land it
    // before reading the DOM so the new state is diffed against the real one.
    this.flushProgressStagger();

    const currentRewards = this.querySelector('[data-ccd-rewards]');
    const incomingRewards = newDrawer.querySelector('[data-ccd-rewards]');

    if (!currentRewards || !incomingRewards) {
      if (currentRewards && !incomingRewards) currentRewards.remove();
      if (!currentRewards && incomingRewards) {
        const body = this.querySelector('[data-ccd-body]');
        if (body) body.prepend(incomingRewards);
      }
      return;
    }

    // Update reward message text
    const currentMsg = currentRewards.querySelector('[data-ccd-rewards-msg]');
    const incomingMsg = incomingRewards.querySelector('[data-ccd-rewards-msg]');
    if (currentMsg && incomingMsg) currentMsg.innerHTML = incomingMsg.innerHTML;

    // Stop order flipped (a $100+ line entered or left the cart, so the
    // milestones swap sides): rebuild the bar from empty with the incoming
    // milestones, then let the normal path below animate it, so it fills to
    // the first icon, pops, and continues, like any other unlock.
    const currentProgress = currentRewards.querySelector('[data-ccd-progress]');
    const incomingProgress = incomingRewards.querySelector('[data-ccd-progress]');
    if (currentProgress && incomingProgress &&
        currentProgress.dataset.ccdOrder !== incomingProgress.dataset.ccdOrder) {
      const fresh = incomingProgress.cloneNode(true);
      fresh.querySelectorAll('.ccd-progress__fill').forEach((fill) => { fill.style.width = '0%'; });
      fresh.querySelectorAll('.ccd-milestone').forEach((ms) => ms.classList.remove('is-unlocked', 'is-just-unlocked'));
      currentProgress.replaceWith(fresh);
      void fresh.offsetWidth; // commit the 0% widths so the fills transition
    }

    const incomingSeg1 = incomingRewards.querySelector('[data-ccd-seg1]');
    const incomingSeg2 = incomingRewards.querySelector('[data-ccd-seg2]');
    const currentSeg1 = currentRewards.querySelector('[data-ccd-seg1]');
    const currentSeg2 = currentRewards.querySelector('[data-ccd-seg2]');
    const ms1 = currentRewards.querySelector('[data-ccd-ms1]');
    const ms2 = currentRewards.querySelector('[data-ccd-ms2]');
    const ms1Unlock = !!incomingRewards.querySelector('[data-ccd-ms1]')?.classList.contains('is-unlocked');
    const ms2Unlock = !!incomingRewards.querySelector('[data-ccd-ms2]')?.classList.contains('is-unlocked');

    // Stage 1: segment 1 fill (CSS transition on width)
    const seg1Changes = currentSeg1 && incomingSeg1 && currentSeg1.style.width !== incomingSeg1.style.width;
    if (seg1Changes) currentSeg1.style.width = incomingSeg1.style.width;

    // Stages 2 + 3: milestone 1 pop, then segment 2 fill + milestone 2
    const popMilestone1 = () => this.updateMilestone(ms1, ms1Unlock);
    const continueToSeg2 = () => {
      if (currentSeg2 && incomingSeg2) currentSeg2.style.width = incomingSeg2.style.width;
      this.updateMilestone(ms2, ms2Unlock);
    };

    // Only sequence when this update crosses milestone 1 while segment 1 is
    // still travelling; every other update (plain increments, removals,
    // re-locks) applies at once so the bar never feels laggy.
    const crossesMilestone1 = ms1Unlock && ms1 && !ms1.classList.contains('is-unlocked') && seg1Changes;
    if (!crossesMilestone1) {
      popMilestone1();
      continueToSeg2();
      return;
    }

    let timer = null;
    const onFillEnd = (e) => {
      if (e.propertyName !== 'width') return;
      cleanup();
      popMilestone1();
      // Let the pop read before the bar moves on (pop animation is 0.4s)
      timer = setTimeout(() => { this._progressStagger = null; continueToSeg2(); }, 200);
    };
    const cleanup = () => {
      currentSeg1.removeEventListener('transitionend', onFillEnd);
      clearTimeout(timer);
    };
    currentSeg1.addEventListener('transitionend', onFillEnd);
    // Fallback: transitionend can be skipped (hidden tab, reduced motion) — land stage 1 anyway
    timer = setTimeout(() => onFillEnd({ propertyName: 'width' }), 650);
    this._progressStagger = {
      cancel: cleanup,
      finish: () => { popMilestone1(); continueToSeg2(); },
    };
  }

  /** Land any pending staged progress-bar update immediately. */
  flushProgressStagger() {
    const pending = this._progressStagger;
    if (!pending) return;
    this._progressStagger = null;
    pending.cancel();
    pending.finish();
  }

  updateMilestone(el, shouldUnlock) {
    if (!el) return;
    const wasUnlocked = el.classList.contains('is-unlocked');
    if (shouldUnlock && !wasUnlocked) {
      el.classList.add('is-unlocked', 'is-just-unlocked');
      el.addEventListener('animationend', () => {
        el.classList.remove('is-just-unlocked');
      }, { once: true });
    } else if (!shouldUnlock && wasUnlocked) {
      el.classList.remove('is-unlocked');
    }
  }

  async refreshSection() {
    try {
      const res = await fetch(`/?sections=${this.sectionId}`);
      const data = await res.json();
      const html = data[this.sectionId];
      if (html) this.replaceDrawerContent(html);
    } catch (err) {
      console.error('Section refresh error:', err);
    }
  }
}

if (!customElements.get('custom-cart-drawer')) {
  customElements.define('custom-cart-drawer', CustomCartDrawer);
}
